<template>
  <div class="tutorial-page">
    <Header></Header>
    
    <div class="hero-section">
      <div class="container-lg">
        <h1 class="main-title">User Tutorial</h1>
        <p class="subtitle">A comprehensive guide to high-performance polygenic score (PGS) methods on PGS-hub</p>
      </div>
    </div>

    <div class="main-body">
      <div class="container-lg max-w-screen-xl">
        <div class="row">
          <aside class="col-lg-3 d-none d-lg-block">
            <div class="sticky-nav">
              <h5 class="nav-header">Contents</h5>
              <ul class="nav-list">
                <li><a href="#overview">Overview</a></li>
                <li><a href="#gwas">GWAS Summary Statistics</a></li>
                <li><a href="#ld">LD References</a></li>
                <li><a href="#single">Single Ancestry</a></li>
                <li><a href="#multiple">Multiple Ancestries</a></li>
                <li><a href="#jobs">Jobs Management</a></li>
              </ul>
            </div>
          </aside>

          <div class="col-lg-9">
            <section id="overview" class="tudiv content-card">
              <div class="section-header">
                <span class="badge">Start</span>
                <h3>Overview</h3>
              </div>
              <p class="navbar-text">
                This tutorial provides a comprehensive guide on using our PGS-hub platform, offering a step-by-step walkthrough for performing <strong>10 high-performance polygenic score (PGS) methods</strong>. You will learn how to obtain SNP estimates efficiently, whether you are a new user or an expert looking to streamline your workflow.
              </p>
            </section>

            <section id="gwas" class="tudiv content-card">
              <div class="section-header">
                <span class="step-num">01</span>
                <h3>GWAS Summary Statistics</h3>
              </div>
              <div class="step-content">
                <p class="navbar-text">First, fill in the metadata (name and description) and upload your GWAS file.</p>
                <div class="img-wrapper shadow-sm">
                  <img :src="gwasSummaryImage1" alt="Upload GWAS">
                </div>
                
                <div class="format-box">
                  <h5 class="format-title"><i class="bi bi-file-earmark-code"></i> Required File Format (Tab-separated)</h5>
                  <div class="img-wrapper">
                    <img :src="gwasSummaryImage2" alt="GWAS Format">
                  </div>
                  <p class="format-desc">
                    <strong>CHR:</strong> Chromosome | <strong>SNP:</strong> rs ID | <strong>A1:</strong> Effect allele | <strong>BETA:</strong> Effect size | <strong>N:</strong> Sample size...
                  </p>
                </div>

                <p class="navbar-text mt-4">Uploaded data is displayed in the panel. Note that data is stored for <strong>30 days</strong> by default (renewable).</p>
                <div class="img-wrapper border-img">
                  <img :src="gwasSummaryImage3" alt="My GWAS Panel">
                </div>
              </div>
            </section>

            <section id="ld" class="tudiv content-card">
              <div class="section-header">
                <span class="step-num">02</span>
                <h3>LD References</h3>
              </div>
              <div class="step-content">
                <div class="alert alert-info-custom">
                  <strong>Note:</strong> We provide pre-computed LD references for <strong>EUR, EAS, SAS, and AFR</strong>. Custom uploads are optional.
                </div>
                <p class="navbar-text">If using custom LD, upload files in <code>pgen</code> format (including .pgen, .psam, .pvar) compressed as <code>.tar.gz</code>.</p>
                <div class="img-wrapper shadow-sm">
                  <img :src="ldReferenceImage1" alt="LD Upload">
                </div>
              </div>
            </section>

            <section id="single" class="tudiv content-card">
              <div class="section-header">
                <span class="step-num">03</span>
                <h3>Single Ancestry Analysis</h3>
              </div>
              <div class="step-content">
                <p class="navbar-text">A simple 3-step process to create parallel jobs for 8 methods including <strong>LDpred2, PRS-CS, and SBayesR</strong>.</p>
                <div class="img-wrapper main-img">
                  <img :src="singleAncestryImage1" alt="Single Ancestry">
                </div>

                <div class="sub-step">
                  <h4 class="h4-step">Step 1: Select GWAS Data</h4>
                  <div class="row align-items-center">
                    <div class="col-md-7"><p>Choose from your uploaded datasets.</p></div>
                    <div class="col-md-5"><img :src="singleAncestryImage2" class="small-img" alt="Step 1"></div>
                  </div>
                </div>

                <div class="sub-step">
                  <h4 class="h4-step">Step 2: Select LD References</h4>
                  <div class="row align-items-center">
                    <div class="col-md-7"><p>We recommend using our tuned 1000G references for optimal results.</p></div>
                    <div class="col-md-5"><img :src="singleAncestryImage3" class="small-img" alt="Step 2"></div>
                  </div>
                </div>

                <div class="sub-step">
                  <h4 class="h4-step">Step 3: Select Algorithms</h4>
                  <p>Choose 1-8 methods. Click "Setting" to tune parameters or use our optimized defaults.</p>
                  <div class="row">
                    <div class="col-md-6"><img :src="singleAncestryImage4" class="inner-img" alt="Algorithms"></div>
                    <div class="col-md-6"><img :src="singleAncestryImage5" class="inner-img" alt="Settings"></div>
                  </div>
                </div>

                <div class="submit-box mt-4">
                  <p>Finally, name your job and click <strong>Submit</strong>.</p>
                  <img :src="singleAncestryImage6" class="wide-img" alt="Submit">
                </div>
              </div>
            </section>

            <section id="multiple" class="tudiv content-card">
              <div class="section-header">
                <span class="step-num">04</span>
                <h3>Multiple Ancestries</h3>
              </div>
              <div class="step-content">
                <p>Features <strong>PRS-CSx</strong> and <strong>X-wing</strong> for cross-population analysis.</p>
                <div class="img-wrapper main-img"><img :src="multipleAncestryImage1" alt="Multi Methods"></div>
                
                <h4 class="h4-step mt-4">Configuration</h4>
                <p>Pair at least two different GWAS datasets with their corresponding LD references.</p>
                <div class="row">
                  <div class="col-md-6"><img :src="multipleAncestryImage2" class="inner-img" alt="Multi Step 1"></div>
                  <div class="col-md-6"><img :src="multipleAncestryImage3" class="inner-img" alt="Multi Step 2"></div>
                </div>
                <img :src="multipleAncestryImage4" class="wide-img mt-3" alt="Multi Submit">
              </div>
            </section>

            <section id="jobs" class="tudiv content-card">
              <div class="section-header">
                <span class="step-num">05</span>
                <h3>Jobs Management</h3>
              </div>
              <div class="step-content">
                <p>Monitor real-time status: <span class="status-tag">Submitted</span> → <span class="status-tag">Started</span> → <span class="status-tag finished">Finished</span>.</p>
                <div class="img-wrapper shadow-sm"><img :src="jobsImage1" alt="Jobs List"></div>
                <p class="mt-4">Click any job to view detailed parameters or download results.</p>
                <div class="img-wrapper shadow-sm"><img :src="jobsImage2" alt="Job Details"></div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/commons/HeaderItem.vue"
export default {
    name:"TutorialItem",
    components: {
        Header
    },
    data() {
        return {
        gwasSummaryImage1: './img/named_pic/gwasSummaryImage1.png',
        gwasSummaryImage2: './img/named_pic/gwasSummaryImage2.png',
        gwasSummaryImage3: './img/named_pic/gwasSummaryImage3.png',
        ldReferenceImage1: './img/named_pic/ldReferenceImage1.png',
        singleAncestryImage1: './img/named_pic/singleAncestryImage1.png',
        singleAncestryImage2: './img/named_pic/singleAncestryImage2.png',
        singleAncestryImage3: './img/named_pic/singleAncestryImage3.png',
        singleAncestryImage4: './img/named_pic/singleAncestryImage4.png',
        singleAncestryImage5: './img/named_pic/singleAncestryImage5.png',
        singleAncestryImage6: './img/named_pic/singleAncestryImage6.png',
        multipleAncestryImage1: './img/named_pic/multipleAncestryImage1.png',
        multipleAncestryImage2: './img/named_pic/multipleAncestryImage2.png',
        multipleAncestryImage3: './img/named_pic/multipleAncestryImage3.png',
        multipleAncestryImage4: './img/named_pic/multipleAncestryImage4.png',
        jobsImage1: './img/named_pic/jobsImage1.png',
        jobsImage2: './img/named_pic/jobsImage2.png'
        };
    }
};
</script>

<style scoped>
/* 基础容器 */
.tutorial-page {
  background-color: #f8fafc;
  color: #1676fd;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

/* Hero Section */
.hero-section {
  padding: 10rem 0 4rem;
  background: linear-gradient(135deg, #0042ac 0%, #334155 100%);
  color: white;
  text-align: center;
  margin-bottom: -4rem;
}
.main-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  
  letter-spacing: -0.025em;
}
.subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 1rem auto 0;
}

/* 主体布局 */
.main-body {
  padding-bottom: 5rem;
}

/* 侧边导航 */
.sticky-nav {
  position: sticky;
  top: 100px;
  padding: 1.5rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
}
.nav-header {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 0.1em;
  margin-bottom: 1rem;
}
.nav-list {
  list-style: none;
  padding: 0;
}
.nav-list li a {
  display: block;
  padding: 0.5rem 0;
  color: #64748b;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s;
}
.nav-list li a:hover {
  color: #3b82f6;
  padding-left: 5px;
}

/* 卡片样式 */
.content-card {
  background: white;
  border-radius: 1rem;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 1rem;
}

.step-num {
  background: #3b82f6;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.badge {
  background: #10b981;
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
}

/* 图片处理 */
.img-wrapper {
  margin: 1.5rem 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #f8fafc;
}
.img-wrapper img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}
.shadow-sm { box-shadow: 0 2px 15px rgba(0,0,0,0.08); }
.border-img { border: 1px solid #e2e8f0; }
.small-img { max-width: 250px; border-radius: 8px; }
.inner-img { max-width: 100%; margin-bottom: 1rem; border-radius: 8px; border: 1px solid #eee; }
.wide-img { width: 100%; border-radius: 8px; }

/* 辅助组件 */
.format-box {
  background: #f1f5f9;
  padding: 1.5rem;
  border-radius: 0.75rem;
  margin: 2rem 0;
}
.format-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}
.format-desc {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 1rem;
}

.alert-info-custom {
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  padding: 1rem;
  color: #1e40af;
  margin-bottom: 1.5rem;
}

.sub-step {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px dashed #e2e8f0;
}
.h4-step {
  font-size: 1.1rem;
  color: #3b82f6;
  margin-bottom: 1rem;
}

.status-tag {
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}
.status-tag.finished { background: #dcfce7; color: #166534; }

/* 文本 */
.navbar-text {
  text-align: justify;
  line-height: 1.7;
  color: #475569;
}

code {
  background: #f1f5f9;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  color: #e11d48;
}

@media (max-width: 768px) {
  .content-card { padding: 1.5rem; }
  .hero-section { padding-top: 8rem; }
}
</style>